/**
 * ============================================================================
 * THE SHOWROOM v4.0 — SEARCH BAR
 * Client-side instant search across all product collections
 * ============================================================================
 *
 * FILE: showroom-search.js (standalone)
 *
 * INSTALLATION:
 * 1. Upload to Shopify Assets
 * 2. Add to the-showroom.liquid AFTER the main showroom JS:
 *    <script src="{{ 'showroom-search.js' | asset_url }}" defer></script>
 *
 * 3. Add the showroom-search-bar.liquid snippet to the-showroom.liquid
 *    BETWEEN {% render 'showroom-filter-bar' %} and the productGrid div.
 *
 * BEHAVIOR:
 * - Searches across ALL collections simultaneously
 * - Inflates all deferred collections on first search (Fix 15 compat)
 * - Matches against: title, subtitle, description, data attributes, ingredients
 * - Does NOT modify activeFilters, spiceFilters, or activeCollection
 * - Clearing search restores previous filter state via showCollection()
 * - Dims the filter bar while search is active (CSS class on parent)
 * - Minimum 2 characters before search activates
 * - Debounced at 200ms for smooth typing
 * - Rotating suggestion overlay: static prefix + cycling intent prompts
 *
 * ============================================================================
 */

(function() {
  'use strict';

  /* ========================================================================
     REFERENCES
     ======================================================================== */

  var searchInput    = document.getElementById('showroomSearchInput');
  var clearBtn       = document.getElementById('showroomSearchClear');
  var banner         = document.getElementById('showroomSearchBanner');
  var bannerText     = document.getElementById('showroomSearchBannerText');
  var bannerClearBtn = document.getElementById('showroomSearchBannerClear');
  var filterSection  = document.querySelector('.tea-spice-filter-section');
  var searchWrap     = document.getElementById('showroomSearch');

  if (!searchInput) return;  // Search bar not in DOM, bail

  var debounceTimer = null;
  var isSearchActive = false;
  var MIN_CHARS = 2;


  /* ========================================================================
     ROTATING SUGGESTIONS
     ======================================================================== */

  var suggestions = [
    'Mediterranean herbs',
    'chamomile',
    'smoky',
    'storm watching',
    'curry',
    'salmom',
    'caffeine free',
    'bergamot',
    'immune teas',
    'barbecue',
    'rooibos',
    'digestion tea',
    'black tea',
    'cinnamon',
    'lavender',
    'sleep',
    'matcha',
    'Parisian Café',
    'ginger',
    'peppercorn',
    'chai',
    'oolong',
    'turmeric',
    'chicken',
    'baking',
    'floral'
  ];

  var cycleContainer = document.getElementById('showroomSuggestionsCycle');
  var suggestionsOverlay = document.getElementById('showroomSuggestions');
  var suggestionEls = [];
  var currentSuggestion = 0;
  var suggestionTimer = null;
  var suggestionsRunning = false;

  /**
   * Build suggestion spans and start rotation
   */
  function initSuggestions() {
    if (!cycleContainer) return;

    // Shuffle suggestions so order varies each page load
    for (var i = suggestions.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var temp = suggestions[i];
      suggestions[i] = suggestions[j];
      suggestions[j] = temp;
    }

    // Create spans inside the cycle wrapper
    suggestions.forEach(function(text) {
      var span = document.createElement('span');
      span.className = 'showroom-search__suggestion';
      span.textContent = text;
      cycleContainer.appendChild(span);
      suggestionEls.push(span);
    });

    // Show the first one
    if (suggestionEls.length > 0) {
      suggestionEls[0].classList.add('active');
      startRotation();
    }
  }

  function startRotation() {
    if (suggestionsRunning) return;
    suggestionsRunning = true;
    suggestionTimer = setInterval(rotateSuggestion, 3000);
  }

  function stopRotation() {
    suggestionsRunning = false;
    if (suggestionTimer) {
      clearInterval(suggestionTimer);
      suggestionTimer = null;
    }
  }

  function rotateSuggestion() {
    if (suggestionEls.length <= 1) return;

    var current = suggestionEls[currentSuggestion];
    current.classList.remove('active');
    current.classList.add('exiting');

    // Clean up exiting class after transition
    setTimeout(function() {
      current.classList.remove('exiting');
    }, 400);

    currentSuggestion = (currentSuggestion + 1) % suggestionEls.length;
    suggestionEls[currentSuggestion].classList.add('active');
  }


  /* ========================================================================
     SUGGESTION VISIBILITY — hide on focus/text, show on blur/empty
     ======================================================================== */

  searchInput.addEventListener('focus', function() {
    stopRotation();
  });

  searchInput.addEventListener('blur', function() {
    if (searchInput.value.trim().length === 0 && !isSearchActive) {
      startRotation();
    }
  });

  // Track text state for CSS class
  searchInput.addEventListener('input', function() {
    if (searchInput.value.trim().length > 0) {
      searchWrap.classList.add('showroom-search--has-text');
      stopRotation();
    } else {
      searchWrap.classList.remove('showroom-search--has-text');
      if (document.activeElement !== searchInput) {
        startRotation();
      }
    }
  });


  /* ========================================================================
     SEARCH LOGIC
     ======================================================================== */

  /**
   * Build searchable text blob for a product card.
   * Pulls from visible text + data attributes + hidden content.
   */
  function getSearchableText(card) {
    var parts = [];

    // Title
    var title = card.querySelector('.product-card-header-title');
    if (title) parts.push(title.textContent);

    // Departure card fields
    var cardTitle = card.querySelector('.card-title');
    if (cardTitle) parts.push(cardTitle.textContent);

    var transportHook = card.querySelector('.transport-hook');
    if (transportHook) parts.push(transportHook.textContent);

    var cardName = card.dataset.cardName;
    if (cardName) parts.push(cardName);

    // Departure card transport hook and experience text
    var hookSubtitle = card.querySelector('.departure-hook-subtitle');
    if (hookSubtitle) parts.push(hookSubtitle.textContent);

    var experienceLine = card.querySelector('.departure-card-experience-line');
    if (experienceLine) parts.push(experienceLine.textContent);

    // Subtitle
    var subtitle = card.querySelector('.product-card-subtitle');
    if (subtitle) parts.push(subtitle.textContent);

    // Description (shop tab)
    var desc = card.querySelector('.shop-description');
    if (desc) parts.push(desc.textContent);

    // Info panel description
    var infoDesc = card.querySelector('.info-description');
    if (infoDesc) parts.push(infoDesc.textContent);

    // Ingredients
    var ingredients = card.querySelector('.info-ingredients');
    if (ingredients) parts.push(ingredients.textContent);

    // Data attributes — tea dimensions
    if (card.dataset.teaType) parts.push(card.dataset.teaType.replace(/[|_-]/g, ' '));
    if (card.dataset.teaEnergy) parts.push(card.dataset.teaEnergy.replace(/[|_-]/g, ' '));
    if (card.dataset.teaPerfectFor) parts.push(card.dataset.teaPerfectFor.replace(/[|_-]/g, ' '));
    if (card.dataset.teaCharacter) parts.push(card.dataset.teaCharacter.replace(/[|_-]/g, ' '));
    if (card.dataset.teaFlavor) parts.push(card.dataset.teaFlavor.replace(/[|_-]/g, ' '));

    // Data attributes — herbal/wellness dimensions
    if (card.dataset.benefitPrimary) parts.push(card.dataset.benefitPrimary.replace(/[|_-]/g, ' '));
    if (card.dataset.benefitSecondary) parts.push(card.dataset.benefitSecondary.replace(/[|_-]/g, ' '));
    if (card.dataset.benefitTertiary) parts.push(card.dataset.benefitTertiary.replace(/[|_-]/g, ' '));

    // Data attributes — spice dimensions
    if (card.dataset.spiceType) parts.push(card.dataset.spiceType.replace(/[|_-]/g, ' '));
    if (card.dataset.spiceProtein) parts.push(card.dataset.spiceProtein.replace(/[|_-]/g, ' '));
    if (card.dataset.spiceDish) parts.push(card.dataset.spiceDish.replace(/[|_-]/g, ' '));
    if (card.dataset.spiceCuisine) parts.push(card.dataset.spiceCuisine.replace(/[|_-]/g, ' '));

    // Data attributes — ingredient guide
    if (card.dataset.ingredientType) parts.push(card.dataset.ingredientType.replace(/[|_-]/g, ' '));
    if (card.dataset.benefitCategory) parts.push(card.dataset.benefitCategory.replace(/[|_-]/g, ' '));

    // Tags
    if (card.dataset.tags) parts.push(card.dataset.tags.replace(/[|_-]/g, ' '));

    // Product handle (catches things like "english-breakfast")
    if (card.dataset.productHandle) parts.push(card.dataset.productHandle.replace(/[|_-]/g, ' '));

    // Territory for departure cards
    if (card.dataset.territory) parts.push(card.dataset.territory.replace(/[|_-]/g, ' '));

    return parts.join(' ').toLowerCase();
  }

  /**
   * Calculate relevance score for a card against search terms.
   * Higher score = more relevant = appears first in results.
   *
   * Scoring tiers:
   *   Title exact match (full query = title)     +100
   *   Title starts with query                     +60
   *   Title contains a search term                +40  per term
   *   Subtitle contains a term                    +25  per term
   *   Data attributes contain a term              +15  per term
   *   Description/ingredients contain a term      +5   per term
   *   Handle/tags contain a term                  +2   per term
   */
  function getRelevanceScore(card, terms, fullQuery) {
    var score = 0;

    // --- Extract text layers ---
    var titleEl = card.querySelector('.product-card-header-title') || card.querySelector('.card-title');
    var titleText = titleEl ? titleEl.textContent.toLowerCase().trim() : '';

    var subtitleEl = card.querySelector('.product-card-subtitle') || card.querySelector('.departure-hook-subtitle');
    var subtitleText = subtitleEl ? subtitleEl.textContent.toLowerCase().trim() : '';

    // Data attributes blob (tea, herbal, spice dimensions)
    var attrParts = [];
    var attrKeys = [
      'teaType', 'teaEnergy', 'teaPerfectFor', 'teaCharacter', 'teaFlavor',
      'benefitPrimary', 'benefitSecondary', 'benefitTertiary',
      'spiceType', 'spiceProtein', 'spiceDish', 'spiceCuisine',
      'ingredientType', 'benefitCategory',
      'cardName', 'territory'
    ];
    attrKeys.forEach(function(key) {
      if (card.dataset[key]) attrParts.push(card.dataset[key].replace(/[|_-]/g, ' '));
    });
    var attrText = attrParts.join(' ').toLowerCase();

    // Description + ingredients blob
    var descParts = [];
    var descEl = card.querySelector('.shop-description');
    if (descEl) descParts.push(descEl.textContent);
    var infoDescEl = card.querySelector('.info-description');
    if (infoDescEl) descParts.push(infoDescEl.textContent);
    var ingredientsEl = card.querySelector('.info-ingredients');
    if (ingredientsEl) descParts.push(ingredientsEl.textContent);
    var experienceEl = card.querySelector('.departure-card-experience-line');
    if (experienceEl) descParts.push(experienceEl.textContent);
    var descText = descParts.join(' ').toLowerCase();

    // Handle + tags blob
    var metaParts = [];
    if (card.dataset.productHandle) metaParts.push(card.dataset.productHandle.replace(/[|_-]/g, ' '));
    if (card.dataset.tags) metaParts.push(card.dataset.tags.replace(/[|_-]/g, ' '));
    var metaText = metaParts.join(' ').toLowerCase();

    // --- Score: title exact match ---
    if (titleText === fullQuery) {
      score += 100;
    }
    // --- Score: title starts with query ---
    else if (titleText.indexOf(fullQuery) === 0) {
      score += 60;
    }

    // --- Score per term across tiers ---
    terms.forEach(function(term) {
      if (titleText.indexOf(term) !== -1)    score += 40;
      if (subtitleText.indexOf(term) !== -1) score += 25;
      if (attrText.indexOf(term) !== -1)     score += 15;
      if (descText.indexOf(term) !== -1)     score += 5;
      if (metaText.indexOf(term) !== -1)     score += 2;
    });

    return score;
  }

  /**
   * Execute the search — show matching cards, hide non-matching.
   * Searches across ALL collections simultaneously.
   * Results sorted by relevance score (highest first),
   * then alphabetically within the same score.
   */
  function executeSearch(query) {
    // Inflate all deferred collections so search covers everything
    if (typeof window.inflateCollection === 'function') {
      ['herbal-wellness-loose-leaf-tea', 'spices-herbs-seasonings', 'teaware-gifts', 'ingredient-guide'].forEach(function(col) {
        window.inflateCollection(col);
      });
    }

    var normalizedQuery = query.toLowerCase().trim();
    var terms = normalizedQuery.split(/\s+/).filter(function(t) { return t.length > 0; });

    if (terms.length === 0 || normalizedQuery.length < MIN_CHARS) {
      clearSearch();
      return;
    }

    isSearchActive = true;

    // Add visual indicator that search is active
    if (filterSection) filterSection.classList.add('showroom-search-active');

    var productGrid = document.getElementById('productGrid');
    var allCards = document.querySelectorAll('.product-card');
    var visibleCount = 0;
    var scoredCards = [];

    allCards.forEach(function(card) {
      // Skip cart view cards
      if (card.classList.contains('cart-view-card')) return;

      var searchText = getSearchableText(card);

      // ALL terms must match (AND logic)
      var allMatch = terms.every(function(term) {
        return searchText.indexOf(term) !== -1;
      });

      if (allMatch) {
        var score = getRelevanceScore(card, terms, normalizedQuery);
        card.classList.remove('hidden');
        scoredCards.push({ card: card, score: score });
        visibleCount++;
      } else {
        card.classList.add('hidden');
      }
    });

    // Sort by relevance (highest first), then alphabetically for ties
    scoredCards.sort(function(a, b) {
      if (b.score !== a.score) return b.score - a.score;
      var tA = (a.card.querySelector('.product-card-header-title') || { textContent: '' }).textContent.toLowerCase();
      var tB = (b.card.querySelector('.product-card-header-title') || { textContent: '' }).textContent.toLowerCase();
      return tA.localeCompare(tB);
    });

    // Re-append in scored order
    scoredCards.forEach(function(item) {
      productGrid.appendChild(item.card);
    });

    // Update banner
    showSearchBanner(query, visibleCount);

    // Update no-results state
    var noResults = document.getElementById('noResults');
    if (noResults) noResults.classList.toggle('active', visibleCount === 0);

    // Re-init carousels for newly visible cards
    if (typeof initializeCarousels === 'function') initializeCarousels();
  }

  /**
   * Clear search and restore previous filter state
   */
  function clearSearch() {
    if (!isSearchActive) return;

    isSearchActive = false;
    searchInput.value = '';
    searchWrap.classList.remove('showroom-search--has-text');

    // Remove visual indicators
    if (filterSection) filterSection.classList.remove('showroom-search-active');
    hideSearchBanner();

    // Restore previous view — showCollection() is IIFE-scoped,
    // so trigger it by clicking the active category button
    var activeBtn = document.querySelector('.category-btn.active');
    if (activeBtn) {
      activeBtn.click();
    }

    searchInput.focus();
  }


  /* ========================================================================
     SEARCH BANNER
     ======================================================================== */

  function showSearchBanner(query, count) {
    if (!banner || !bannerText) return;

    bannerText.innerHTML =
      '<strong>' + count + '</strong> result' + (count !== 1 ? 's' : '') +
      ' for "<strong>' + escapeHtml(query) + '</strong>"';

    banner.classList.add('active');
  }

  function hideSearchBanner() {
    if (banner) banner.classList.remove('active');
  }


  /* ========================================================================
     EVENT HANDLERS
     ======================================================================== */

  // Debounced input handler
  searchInput.addEventListener('input', function() {
    clearTimeout(debounceTimer);
    var query = searchInput.value.trim();

    if (query.length < MIN_CHARS) {
      if (isSearchActive) clearSearch();
      return;
    }

    debounceTimer = setTimeout(function() {
      executeSearch(query);
    }, 200);
  });

  // Clear button (×) inside input
  clearBtn.addEventListener('click', function(e) {
    e.preventDefault();
    e.stopPropagation();
    clearSearch();
  });

  // Clear button in banner
  bannerClearBtn.addEventListener('click', function(e) {
    e.preventDefault();
    e.stopPropagation();
    clearSearch();
  });

  // Escape key clears search
  searchInput.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      clearSearch();
      searchInput.blur();
    }
  });

  // Clear search when user clicks a category button
  document.addEventListener('click', function(e) {
    var categoryBtn = e.target.closest('.category-btn');
    if (categoryBtn && isSearchActive) {
      clearSearch();
    }
  }, true);  // Capture phase — fires before the main handler

  // Clicking the suggestion overlay focuses the input
  if (suggestionsOverlay) {
    suggestionsOverlay.addEventListener('click', function() {
      searchInput.focus();
    });
    suggestionsOverlay.style.pointerEvents = 'auto';
    suggestionsOverlay.style.cursor = 'text';
  }


  /* ========================================================================
     UTILITY
     ======================================================================== */

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str || ''));
    return div.innerHTML;
  }


  /* ========================================================================
     INIT
     ======================================================================== */

  initSuggestions();

})();